import type { FieldErrors, UseFormRegisterReturn } from 'react-hook-form';
import { Checkbox } from '@/components/ui/Checkbox';
import { TextareaField } from '@/components/forms/TextareaField';
import { SelectField } from '@/components/forms/SelectField';
import { FieldLabel } from '@/components/forms/FieldLabel';
import {
  AI_CLASSIFICATION_OPTIONS,
  FIELD_LABELS,
  FIELD_PLACEHOLDERS,
  INVESTOR_TYPE_OPTIONS,
  PAYING_CUSTOMERS_OPTIONS,
  REVENUE_GROWTH_RATE_OPTIONS,
  REVENUE_MODEL_OPTIONS,
  SELECT_PLACEHOLDER,
} from '../constants';

/** The matching-critical business fields both startup forms collect, by form field name. */
export interface StartupBusinessFieldValues {
  aiClassification?: string;
  revenueModel?: string;
  revenueGrowthRate?: string;
  payingCustomers?: string;
  useOfFunds?: string;
  investorTypesSought?: string[];
}

export type StartupBusinessFieldName = keyof StartupBusinessFieldValues;

export interface StartupBusinessFieldsProps {
  /** The host form's `register`, narrowed to these fields. */
  register: (name: StartupBusinessFieldName) => UseFormRegisterReturn;
  errors?: FieldErrors<StartupBusinessFieldValues>;
}

const SELECTS = [
  { name: 'aiClassification', options: AI_CLASSIFICATION_OPTIONS },
  { name: 'revenueModel', options: REVENUE_MODEL_OPTIONS },
  { name: 'revenueGrowthRate', options: REVENUE_GROWTH_RATE_OPTIONS },
  { name: 'payingCustomers', options: PAYING_CUSTOMERS_OPTIONS },
] as const;

/** AI Classification, Revenue Model, Revenue Growth Rate and Paying Customers. */
export function StartupMetricFields({ register, errors }: StartupBusinessFieldsProps) {
  return (
    <div className="grid gap-5 sm:grid-cols-2">
      {SELECTS.map(({ name, options }) => (
        <SelectField
          key={name}
          {...register(name)}
          label={FIELD_LABELS[name]}
          placeholder={`${FIELD_LABELS[name]} - ${SELECT_PLACEHOLDER}`}
          options={options}
          error={errors?.[name]?.message}
        />
      ))}
    </div>
  );
}

/**
 * Use of Funds and Investor Types Sought. The profile editor shows these in
 * the Current Raise card (profile-redesign FR-172), apart from the metrics.
 */
export function StartupRaiseIntentFields({ register, errors }: StartupBusinessFieldsProps) {
  return (
    <>
      <TextareaField
        {...register('useOfFunds')}
        label={FIELD_LABELS.useOfFunds}
        rows={2}
        placeholder={FIELD_PLACEHOLDERS.useOfFunds}
        error={errors?.useOfFunds?.message}
      />

      <fieldset className="flex flex-col gap-2">
        <FieldLabel label={FIELD_LABELS.investorTypesSought} variant="caps" />
        <div className="flex flex-wrap gap-4">
          {INVESTOR_TYPE_OPTIONS.map((option) => (
            <Checkbox
              key={option.value}
              value={option.value}
              label={option.label}
              {...register('investorTypesSought')}
            />
          ))}
        </div>
        {errors?.investorTypesSought?.message && (
          <p role="alert" className="pl-1 text-xs text-danger">
            {errors.investorTypesSought.message}
          </p>
        )}
      </fieldset>
    </>
  );
}

/**
 * All six matching-critical business fields - shared by the create wizard and
 * the startup profile editor (UX sheet r228) so the two forms stay at parity.
 *
 * Every select keeps its placeholder option on purpose: choosing it clears the
 * field, which the editor sends as `null`.
 */
export function StartupBusinessFields(props: StartupBusinessFieldsProps) {
  return (
    <>
      <StartupMetricFields {...props} />
      <StartupRaiseIntentFields {...props} />
    </>
  );
}
